import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { sanityClient } from "../lib/sanityClient";
import { Helmet } from "react-helmet-async";
import { PortableText } from "@portabletext/react";

const ArticleDetailsPage = () => {
  const { slug, lang = "en" } = useParams(); // get both :lang and :slug from URL
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const query = `*[_type == "article" && slug.${lang}.current == $slug][0]{
          title,
          slug,
          author,
          publishedAt,
          primaryImage,
          additionalImages,
          content
        }`;
        const fetchedArticle = await sanityClient.fetch(query, { slug });
        setArticle(fetchedArticle);
      } catch (error) {
        console.error("Error fetching article:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticle();
  }, [slug, lang]);

  if (loading) return <p className="text-center text-gray-500">Loading...</p>;
  if (!article) return <p className="text-center text-red-500">Article not found.</p>;

  const localizedTitle = article.title?.[lang] || article.title?.en;
  const localizedAuthor = article.author?.[lang] || article.author?.en;
  const localizedContent = article.content?.[lang] || article.content?.en;

  const components = {
    types: {
      image: ({ value }) => (
        <img
          src={value.asset?.url}
          alt={value.alt || "Article image"}
          className="w-full rounded-lg my-4"
        />
      ),
    },
    marks: {
      link: ({ children, value }) => (
        <a
          href={value.href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-500 underline"
        >
          {children}
        </a>
      ),
    },
    block: {
      h1: ({ children }) => <h1 className="text-3xl font-bold mt-6">{children}</h1>,
      h2: ({ children }) => <h2 className="text-2xl font-semibold mt-4">{children}</h2>,
      normal: ({ children }) => <p className="text-lg leading-relaxed">{children}</p>,
    },
  };

  return (
    <div className="container mx-auto p-6">
      <Helmet>
        <title>{localizedTitle} - 237Mechanic</title>
        <meta name="description" content={localizedTitle} />
         <link rel="canonical" href={`https://237mechanic.com/cm/en/articles/${slug}`} />
      </Helmet>

      <h1 className="text-3xl font-bold mb-4">{localizedTitle}</h1>
      <p className="text-gray-500 mb-4">
        {localizedAuthor} | {new Date(article.publishedAt).toLocaleDateString()}
      </p>

      {article.primaryImage && (
        <img
          src={article.primaryImage}
          alt={localizedTitle}
          className="w-full h-80 object-cover mb-6 rounded-lg"
        />
      )}

      <div className="prose max-w-full text-gray-800">
        <PortableText value={localizedContent} components={components} />
      </div>

      {article.additionalImages?.length > 0 && (
        <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
          {article.additionalImages.map((img, index) => (
            <img key={index} src={img} alt={`Additional ${index}`} className="rounded-lg" />
          ))}
        </div>
      )}
    </div>
  );
};

export default ArticleDetailsPage;
